import React, { useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Paper,
} from '@mui/material';
import {
  Palette,
  CalendarToday,
  People,
  EmojiEvents,
  Share,
  Favorite
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Custom styled components
const GradientHeader = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(to right, #9333ea, #3b82f6)',
  color: 'white',
  padding: theme.spacing(8, 0),
}));

const CreatorCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'box-shadow 0.3s',
  '&:hover': {
    boxShadow: theme.shadows[10],
  },
}));

const CollabEventPage = () => {
  // Event details data
  const eventDetails = {
    title: "AIアートクリエイターズ コラボフェス 2024",
    period: "2024年12月25日 〜 2025年1月25日",
    description: "トップクリエイターとコラボレーションして、あなただけの特別なAIアートを作り出そう！",
    prizes: [
      "グランプリ: オリジナルグッズセット & トップページ掲載",
      "準グランプリ: プレミアムメンバーシップ1年分",
      "特別賞: 限定プロフィールバッジ"
    ]
  };

  // Creators data
  const creators = [
    {
      name: "桜井 杏",
      id: "@sakurai_an",
      specialty: "ファンタジーイラスト",
      works: 120,
      followers: "12.5k"
    },
    {
      name: "高橋 創",
      id: "@takahashi_sou",
      specialty: "SF・メカデザイン",
      works: 85,
      followers: "8.9k"
    },
    {
      name: "山田 陽子",
      id: "@yamada_yoko",
      specialty: "キャラクターデザイン",
      works: 200,
      followers: "15.2k"
    }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header Section */}
      <GradientHeader>
        <Container maxWidth="lg">
          <Box textAlign="center">
            <Typography variant="h3" component="h1" gutterBottom>
              {eventDetails.title}
            </Typography>
            <Typography variant="h6" paragraph>
              {eventDetails.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button variant="contained" sx={{ bgcolor: 'white', color: '#9333ea', '&:hover': { bgcolor: '#f3e8ff' } }}>
                参加登録
              </Button>
              <Button variant="outlined" sx={{ color: 'white', borderColor: 'white' }}>
                詳細を見る
              </Button>
            </Box>
          </Box>
        </Container>
      </GradientHeader>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Event Overview */}
        <Card sx={{ mb: 6 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday color="primary" />
                <Typography variant="h6">イベント概要</Typography>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>開催期間</Typography>
                <Typography color="text.secondary" paragraph>
                  {eventDetails.period}
                </Typography>
                <Typography variant="h6" gutterBottom>参加方法</Typography>
                <List>
                  {[
                    '参加登録フォームから登録',
                    '好きなクリエイターを選択',
                    'コラボ作品を制作・投稿',
                    'ハッシュタグ「#AIArtCollab2024」を付けて投稿'
                  ].map((step, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={`${index + 1}. ${step}`} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>賞品</Typography>
                <List>
                  {eventDetails.prizes.map((prize, index) => (
                    <ListItem key={index}>
                      <ListItemIcon>
                        <EmojiEvents sx={{ color: 'gold' }} />
                      </ListItemIcon>
                      <ListItemText primary={prize} />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Creators Section */}
        <Card sx={{ mb: 6 }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <People color="primary" />
                <Typography variant="h6">参加クリエイター</Typography>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={3}>
              {creators.map((creator, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <CreatorCard>
                    <CardContent>
                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 80,
                            height: 80,
                            margin: '0 auto',
                            bgcolor: 'primary.light',
                          }}
                        >
                          <Palette />
                        </Avatar>
                        <Typography variant="h6" sx={{ mt: 2 }}>
                          {creator.name}
                        </Typography>
                        <Typography color="text.secondary">
                          {creator.id}
                        </Typography>
                      </Box>
                      <List dense>
                        <ListItem>
                          <ListItemText 
                            primary={`専門: ${creator.specialty}`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary={`作品数: ${creator.works}`}
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemText 
                            primary={`フォロワー: ${creator.followers}`}
                          />
                        </ListItem>
                      </List>
                      <Button variant="outlined" fullWidth sx={{ mt: 2 }}>
                        プロフィールを見る
                      </Button>
                    </CardContent>
                  </CreatorCard>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Share Buttons */}
        <Box textAlign="center">
          <Typography variant="h6" gutterBottom>
            イベントをシェアする
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" startIcon={<Share />}>
              Twitter
            </Button>
            <Button variant="outlined" startIcon={<Favorite />}>
              いいね
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Footer */}
      <Box component="footer" sx={{ bgcolor: 'grey.100', py: 4, mt: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2024 AIアートクリエイターズ コラボフェス All Rights Reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default CollabEventPage;